/*!
 * © 2024. The EGT Universe. All Rights Reserved.
 * License: Requires ACTIVE AppArtisan Subscription.
 * For more information visit: https://egtuniverse.com/legal
 *
 * Created for Universe App Tools.
 */

/*
 * renderChildren — render an ORDERED array of email.* nodes into concatenated
 * { html, text } using the SAME per-node contract as MailMessage.buildEmail
 * (resolve the namespace in the registry, call the element's renderEmail, collect
 * the row html + the plain-text part).
 *
 * WHY THIS EXISTS: MailMessage.buildEmail flat-iterates a template's top-level
 * `inner` array and has no notion of nesting. The container primitives
 * (email.card, email.section) need to render THEIR OWN children so an email can
 * group blocks into rounded, padded surfaces instead of one flat stack. Rather
 * than teach MailMessage about containers (a change to the shared assembly that
 * every tenant's email flows through), the containers call this helper with the
 * registry they were built against. buildEmail is left byte-for-byte untouched.
 *
 * Each leaf element's renderEmail returns a `<tr>…</tr>` row, so the caller places
 * the concatenated rows inside its own nested <table>. A node whose namespace is
 * unknown or has no renderEmail is skipped with an HTML comment (never a visible
 * error row inside a card) — the top-level buildEmail still fails-closed on
 * unrenderable TOP-LEVEL nodes, and a card's children are authored, not user data.
 *
 * Nesting: a container passed as a child works because the child container's own
 * renderEmail re-requires the registry lazily and calls this helper again.
 */

/**
 * @param {object} registry  The element registry (must expose .search(namespace)).
 * @param {Array}  nodes      Array of email.* template nodes (each { namespace|n, ... }).
 * @returns {Promise<{ html: string, text: string }>}
 */
async function renderChildren(registry, nodes) {
  var out = { html: "", textParts: [] };
  if (!registry || typeof registry.search !== "function" || !Array.isArray(nodes)) {
    return { html: "", text: "" };
  }

  for (var i = 0; i < nodes.length; i++) {
    var item = nodes[i];
    var ns = (item && (item.namespace || item.n)) ? (item.namespace || item.n) : "";
    if (!ns) { continue; }

    var element = registry.search(ns);
    if (!element || typeof element.renderEmail !== "function") {
      out.html += "<!-- email child not renderable: " + String(ns).replace(/-->/g, "") + " -->";
      continue;
    }

    try {
      var result = await element.renderEmail(item);
      out.html += (result && result.html) ? result.html : "";
      if (result && result.text) { out.textParts.push(result.text); }
    } catch (e) {
      out.html += "<!-- email child render error: " + String(ns).replace(/-->/g, "") + " -->";
    }
  }

  return { html: out.html, text: out.textParts.join("\n") };
}

module.exports = { renderChildren: renderChildren };
